"use client";

import { FC, useRef } from "react";
import { useWindowSize } from "react-use";
import { usePathname } from "next/navigation";
import { useGSAP } from "@gsap/react";
import { gsap } from "gsap";

import { useScreens } from "@Shared/hooks";
import { ElProps } from "@Shared/types";
import { twcx } from "@Shared/utils";

const SIZE = 10;
const SIZE_ON_HOVER = 60;
const HOVER_SELECTORS = "[data-cursor-hover], a, button";

export type CursorType = ElProps<"div">;

export const Cursor: FC<CursorType> = ({ className, ...restProps }) => {
    const refComponent = useRef<HTMLDivElement>(null);

    const pathname = usePathname();
    const { width } = useWindowSize();
    const { isLg } = useScreens();

    useGSAP(
        (ctx, ctxSafe) => {
            if (!refComponent.current || !isLg || !ctxSafe) {
                return;
            }

            const initialScale = SIZE / SIZE_ON_HOVER;

            const move = ctxSafe((x: number, y: number) => {
                gsap.to(refComponent.current, {
                    duration: 0.75,
                    x,
                    y,
                });
            });

            const scale = ctxSafe((scale: number) => {
                gsap.to(refComponent.current, {
                    scale,
                });
            });

            const handleMouseHover = (e: MouseEvent) => {
                refComponent.current?.classList.toggle("opacity-0", e.type === "mouseleave");

                if (e.type === "mouseenter") {
                    move(e.clientX, e.clientY);
                }
            };

            const handleMouseMove = (e: MouseEvent) => {
                refComponent.current?.classList.remove("opacity-0");

                move(e.clientX, e.clientY);
            };

            const handleHoverSelectors = (e: Event) => {
                scale(e.type === "mouseenter" ? 1 : initialScale);
            };

            gsap.set(refComponent.current, {
                x: innerWidth / 2,
                y: innerHeight / 2,
                scale: initialScale,
            });

            document.addEventListener("mouseenter", handleMouseHover);
            document.addEventListener("mouseleave", handleMouseHover);
            document.addEventListener("mousemove", handleMouseMove);

            document.querySelectorAll(HOVER_SELECTORS).forEach(el => {
                el.addEventListener("mouseenter", handleHoverSelectors);
                el.addEventListener("mouseleave", handleHoverSelectors);
            });

            return () => {
                refComponent.current?.classList.add("opacity-0");

                document.removeEventListener("mouseenter", handleMouseHover);
                document.removeEventListener("mouseleave", handleMouseHover);
                document.removeEventListener("mousemove", handleMouseMove);

                document.querySelectorAll(HOVER_SELECTORS).forEach(el => {
                    el.removeEventListener("mouseenter", handleHoverSelectors);
                    el.removeEventListener("mouseleave", handleHoverSelectors);
                });
            };
        },
        {
            scope: refComponent,
            revertOnUpdate: true,
            dependencies: [pathname, width, isLg],
        }
    );

    return (
        <div
            ref={refComponent}
            className={twcx(
                "pointer-events-none fixed left-0 top-0 z-[999] select-none rounded-full bg-white opacity-0 mix-blend-difference transition-opacity",
                className
            )}
            style={{
                marginTop: -SIZE_ON_HOVER / 2,
                marginLeft: -SIZE_ON_HOVER / 2,
                width: SIZE_ON_HOVER,
                height: SIZE_ON_HOVER,
            }}
            {...restProps}
        />
    );
};
